import ListingTable from 'common/components/listingTable/ListingTable';
import ListingTableCache, { ListingTableData } from 'common/components/listingTable/ListingTableCache';
import TableActionButton from 'common/components/listingTable/actionButton/TableActionButton';
import { FilterData, ListingTableColumn, OrderData } from 'common/components/listingTable/models';
import ListingTableSidebar from 'common/components/listingTable/sidebar/ListingTableSidebar';
import { orderColumns } from 'common/components/listingTable/tableUtils';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Logger from 'common/services/Logger';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { DATE_TIME_FORMAT_DEFAULT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import Button from 'common/components/button/Button';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import Loading from 'common/services/Loading';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import Toast from 'common/services/Toast';
import DateFormat from 'common/components/dateFormat/dateFormat';
import BudgetComponentsService, { BudgetComponentsSearchCriteria } from 'api/budgetComponents/BudgetComponentsService';
import { BudgetComponentDto } from 'api/budgets/models/BudgetComponentDto';
import FamiliesService from 'api/families/FamiliesService';
import { FamilyDto } from 'api/families/models/FamilyDto';
import { SelectInputOption } from 'common/components/selectInput/SelectInput';

const PAGE_ID = 'BUSINESS_BUDGETS_COMPONENTS_LIST';
const baseUrl = '/business/budgets-components';

interface ComponentListItem {
    id: string;
    description: string;
    familyId?: string | null;
    familyName?: string;
    subFamilyId?: string | null;
    subFamilyName?: string;
    updatedDate?: Date;
    userUpdatedName?: string;
    createdDate?: Date;
    userCreatedName?: string;
}

function BudgetsComponentsListScreen(): JSX.Element | null {
    const { t } = useTranslation();
    const [data, setData] = useState<ComponentListItem[]>([]);
    const [tableData, setTableData] = useState<ListingTableData<ComponentListItem, Partial<BudgetComponentsSearchCriteria>>>(ListingTableCache.get(PAGE_ID, {
        order: { field: 'description', isOrderAsc: true },
        hiddenColumns: [],
        columnsOrder: [],
        columnsWidth: [],
        page: 1,
        filters: {},
        lastRowId: null,
    }));
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState<ComponentListItem | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_READ'])
    const [totalItems, setTotalItems] = useState(0);
    const [scrollToRowId, setScrollToRowId] = useState<string | null>(null);
    const firstLoad = useRef(true);
    const [families, setFamilies] = useState<SelectInputOption[]>([]);
    const [subFamilies, setSubFamilies] = useState<SelectInputOption[]>([]);

    const loadFamilies = async () => {
        try {
            const familiesData = await FamiliesService.getElementsCatalog({ familyIds: [], isElement: false, isComponent: true });

            setFamilies(familiesData.filter((x: FamilyDto) => !x.parentId).map((x: FamilyDto) => ({
                label: x.name,
                value: x.id
            })));

            const subFamiliesArray: SelectInputOption[] = [];
            familiesData.filter((x: FamilyDto) => !x.parentId).forEach((x: FamilyDto) => {
                const children = familiesData.filter((y: FamilyDto) => y.parentId === x.id);
                children.forEach((y: FamilyDto) => {
                    subFamiliesArray.push({ label: y.name, value: y.id, subTitle: x.name });
                });
            });
            setSubFamilies(subFamiliesArray);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t load families', error);
        }
    };

    const columns: ListingTableColumn<ComponentListItem>[] = orderColumns([
        {
            name: t('budgets.list.description'),
            field: 'description',
            orderColumn: 'description',
            filter: { type: 'text', value: tableData.filters.description ?? '', placeholder: t('budgets.list.description') },
            minWidth: '18rem',
        },
        {
            name: t('elements.family'),
            field: 'familyId',
            minWidth: '18rem',
            filter: { type: 'dropdown', value: (tableData.filters as any).familyId ?? '', options: families },
            hideOrderButton: true,
            renderCell: (row) => row.familyName ?? '',
        },
        {
            name: t('elements.sub_family'),
            field: 'subFamilyId',
            minWidth: '18rem',
            filter: { type: 'dropdown', value: (tableData.filters as any).subFamilyId ?? '', options: subFamilies },
            hideOrderButton: true,
            renderCell: (row) => row.subFamilyName ?? '',
        },
        {
            name: t('common.updated_date'),
            field: 'updatedDate',
            filter: {
                type: 'date-range',
                value: {
                    start: tableData.filters.updatedDateStart ? new Date(tableData.filters.updatedDateStart) : null,
                    end: tableData.filters.updatedDateEnd ? new Date(tableData.filters.updatedDateEnd) : null
                }
            },
            minWidth: '22rem',
            orderColumn: 'updated_date',
            renderCell: (row) => {
                return row.updatedDate ? <DateFormat value={row.updatedDate} format={DATE_TIME_FORMAT_DEFAULT} /> : '';
            }
        },
        {
            name: t('common.user_updated'),
            field: 'userUpdatedName',
            filter: { type: 'text', value: tableData.filters.userUpdatedName ?? '', placeholder: t('common.user_updated') },
            minWidth: '18rem',
            orderColumn: 'user_updated_name',
        },
    ], tableData.columnsOrder);

    const onFilter = (col: ListingTableColumn<ComponentListItem>, filterData: FilterData) => {
        setTableData(d => {
            const data = ({ ...d, page: 1 });
            if (col.field === 'description') {
                data.filters = { ...data.filters, description: filterData.value as string };
            }
            if (col.field === 'familyId') {
                (data.filters as any).familyId = filterData.value as string;
            }
            if (col.field === 'subFamilyId') {
                (data.filters as any).subFamilyId = filterData.value as string;
            }
            if (col.field === 'updatedDate') {
                data.filters = {
                    ...data.filters,
                    updatedDateStart: filterData.value?.start as Date,
                    updatedDateEnd: filterData.value?.end as Date,
                };
            }
            if (col.field === 'userUpdatedName') {
                data.filters = { ...data.filters, userUpdatedName: filterData.value as string };
            }
            return { ...data };
        });
        setRefreshKey(k => k + 1);
    }

    const onOrder = (orderData: OrderData<ComponentListItem>) => {
        setRefreshKey(k => k + 1);
        setTableData(d => ({ ...d, order: orderData, page: 1 }));
    }

    const onClearFilters = () => {
        setTableData(d => ({ ...d, page: 1, filters: {}, lastRowId: null }));
        setRefreshKey(k => k + 1);
    }

    const renderAction = (row: ComponentListItem) => {
        return (<TableActionButton
            options={[
                { label: <span><FaEdit /> {t('common.edit')}</span>, href: baseUrl + '/edit/' + row.id, onClick: () => ListingTableCache.saveLastRowId(PAGE_ID, row.id) },
                { label: <span><FaTrash /> {t('common.delete')}</span>, onClick: () => setShowConfirmDeleteModal(row) },
            ]}
        />)
    }

    const getData = async () => {
        if (isLoading) {
            return;
        }

        try {
            setIsLoading(true);

            const result = await BudgetComponentsService.getList({
                ...tableData.filters,
                page: firstLoad.current && tableData.page > 1 ? 1 : tableData.page,
                itemsPerPage: firstLoad.current && tableData.page > 1 ? DEFAULT_PAGINATION_ITEMS_PER_PAGE * tableData.page : DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                orderColumn: tableData.order.orderColumn,
                orderBy: tableData.order.isOrderAsc ? 'asc' : 'desc',
            });

            const newListElements = result.currentPage === 1 ? result.items : [...data, ...result.items];
            setHasMore(result.totalItems > newListElements.length);
            setData(newListElements);
            setTotalItems(result.totalItems);

            if (firstLoad.current) {
                setScrollToRowId(tableData.lastRowId);
            }
            firstLoad.current = false;
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get budget components', error);
            Toast.error(t('messages.error_load_info'));
        }

        setIsLoading(false);
    }

    const onDelete = async (result: boolean) => {
        if (!result) {
            setShowConfirmDeleteModal(null)
            return;
        }

        if (!showConfirmDeleteModal) {
            return;
        }

        try {
            Loading.show();

            await BudgetComponentsService.remove({ id: showConfirmDeleteModal.id, version: '' })

            Loading.hide();
            Toast.success(t('messages.record_delete_success'));

            setTableData(d => ({ ...d, page: 1 }));
            setRefreshKey(k => k + 1);
        } catch (error: any) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete budget component', error);
            Toast.error(t('messages.record_delete_error'));
        }

        setShowConfirmDeleteModal(null)
    }

    const onLoadMore = () => {
        setTableData(d => ({ ...d, page: d.page + 1 }));
    }

    const onRowClick = (id: string) => {
        ListingTableCache.saveLastRowId(PAGE_ID, id);
    }

    useEffect(() => {
        void loadFamilies();
    }, []);

    useEffect(() => {
        void getData();
    }, [tableData.page, tableData.filters, tableData.order, refreshKey]);

    useEffect(() => {
        ListingTableCache.save(PAGE_ID, tableData);
    }, [tableData]);

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('budgets.list.components_title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('budgets.list.components_title'), url: baseUrl },
                ]}
            />
            <PageHeader title={t('budgets.list.components_title')} subTitle={`${totalItems} ${t('common.total_results')}`} informationText={t('common.list')} addSidebarSpacing>
                {canWrite && <Button href={baseUrl + '/create'}>{t('common.new')}</Button>}
            </PageHeader>
            <PageContainer>
                <ListingTable<ComponentListItem>
                    columns={columns}
                    rows={data}
                    order={tableData.order}
                    hiddenColumns={tableData.hiddenColumns}
                    columnsWidths={tableData.columnsWidth}
                    onOrder={onOrder}
                    onFilter={onFilter}
                    onClearFilters={onClearFilters}
                    onRenderAction={canWrite ? renderAction : undefined}
                    infiniteScroll={{
                        isLoading,
                        hasMore,
                        onLoadMore,
                    }}
                    onRowClick={(r) => onRowClick(r.id)}
                    onHref={(r) => baseUrl + '/details/' + r.id}
                    onRowId={r => r.id}
                    scrollToRowId={scrollToRowId}
                    onColResize={(w) => setTableData(d => ({ ...d, columnsWidth: w }))}
                />
            </PageContainer>
            <ListingTableSidebar
                columns={columns}
                hiddenColumns={tableData.hiddenColumns}
                onChangeHiddenColumns={(hc) => setTableData(d => ({ ...d, hiddenColumns: hc }))}
                onChangeColumnsOrder={(oc) => setTableData(d => ({ ...d, columnsOrder: oc }))}
            />
            <ConfirmDeleteModal
                itemName={showConfirmDeleteModal?.description}
                isOpen={!!showConfirmDeleteModal}
                onClose={onDelete}
            />
        </ScreenTitle>
    );
}

export default BudgetsComponentsListScreen;
